/**
 * Welcome Onboarding
 * 
 * 최초 접근자를 위한 전체 화면 온보딩
 * - 서비스 가치 전달에 초점
 * - 플랜 홍보는 하지 않음 (나중에 자연스럽게 유도)
 */

import { useState, useEffect } from 'react';
import { useLanguage } from '../i18n';
import { useBodyScrollLock } from '../hooks';
import { analytics } from '../analytics';
import LogoAnimation from './LogoAnimation';

interface WelcomeOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
  onJustBrowse: () => void;
}

export default function WelcomeOnboarding({ isOpen, onJustBrowse }: WelcomeOnboardingProps) {
  const { lang } = useLanguage();
  const [currentFeature, setCurrentFeature] = useState(0);

  const isKo = lang === 'ko';

  // 자동 슬라이드
  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(timer);
  }, [isOpen]);

  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const handleStart = () => {
    analytics.welcomeJustBrowse();
    onJustBrowse();
  };

  // 서비스 핵심 가치
  const features = [
    {
      icon: '📋',
      title: isKo ? '기획 의도 분석' : 'Planning Analysis',
      description: isKo 
        ? '기획 문서를 AI가 분석하여 핵심 목표와 위험 요소를 파악합니다.'
        : 'AI analyzes planning documents to identify key goals and risks.',
      color: 'var(--neo-lime)',
    },
    {
      icon: '🧪',
      title: isKo ? 'QA 검증 상태 점검' : 'QA Verification Check',
      description: isKo 
        ? '테스트 케이스 결과를 종합하여 검증 완성도를 평가합니다.'
        : 'Evaluates test coverage by analyzing test case results.',
      color: 'var(--neo-mint)',
    },
    {
      icon: '🚀',
      title: isKo ? 'AI 릴리즈 판단' : 'AI Release Decision',
      description: isKo 
        ? 'GO / CONDITIONAL / HOLDING / NO-GO 명확한 판단을 제공합니다.'
        : 'Provides clear GO / CONDITIONAL / HOLDING / NO-GO decisions.',
      color: 'var(--neo-pink)',
    },
  ];

  // 신뢰 지표
  const stats = [
    { value: '3', label: isKo ? '단계 분석' : 'Step Analysis' },
    { value: 'AI', label: isKo ? '기반 판단' : 'Powered' },
    { value: '₩0', label: isKo ? '으로 시작' : 'to start' },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto bg-gradient-to-br from-[#0a0a1a] via-[#1a1a2e] to-[#0a0a1a]">
      {/* 배경 패턴 */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, var(--neo-mint) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>
      
      {/* 그라데이션 오버레이 */}
      <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[var(--neo-mint)]/10 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-[var(--neo-pink)]/10 to-transparent pointer-events-none" />

      <div className="relative w-full max-w-2xl mx-auto p-6 my-8">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* 로고 - Lottie 애니메이션 */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-6 bg-slate-900 rounded-lg shadow-[4px_4px_0_0_black] overflow-hidden flex items-center justify-center">
              <LogoAnimation size={96} loop={false} />
            </div>
            
            {/* 메인 타이틀 */}
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-4">
              {isKo ? '릴리즈, 확신을 가지고' : 'Release with Confidence'}
            </h1>
            
            <p className="text-lg text-white/70 max-w-md mx-auto leading-relaxed">
              {isKo 
                ? '기획 → 리스크 → QA 검증을 AI가 분석하여\n출시 결정의 불확실성을 줄여드립니다.'
                : 'AI analyzes Planning → Risk → QA to\nreduce uncertainty in release decisions.'}
            </p>
          </div>

          {/* 핵심 기능 카드 슬라이더 */}
          <div className="mb-8">
            <div className="relative bg-white/5 border border-white/10 p-6 min-h-[140px]">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className={`transition-all duration-500 ${
                    idx === currentFeature 
                      ? 'opacity-100 translate-x-0' 
                      : 'opacity-0 absolute inset-6 translate-x-4'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-14 h-14 flex items-center justify-center flex-shrink-0 shadow-[2px_2px_0_0_black]"
                      style={{ backgroundColor: feature.color }}
                    >
                      <span className="text-2xl">{feature.icon}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                      <p className="text-sm text-white/60 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* 인디케이터 */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {features.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentFeature(idx)}
                    className={`w-2 h-2 transition-all ${
                      idx === currentFeature 
                        ? 'w-6 bg-[var(--neo-mint)]' 
                        : 'bg-white/20 hover:bg-white/40'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* 통계 */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            {stats.map((stat, idx) => (
              <div 
                key={idx} 
                className="text-center p-4 bg-white/5 border border-white/10"
              >
                <div className="text-2xl font-black text-[var(--neo-mint)] mb-1">{stat.value}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* CTA 버튼 */}
          <div className="space-y-4">
            <button
              onClick={handleStart}
              className="w-full py-4 bg-[var(--neo-mint)] text-black text-lg font-bold hover:brightness-110 transition-all shadow-[4px_4px_0_0_black] active:shadow-[2px_2px_0_0_black] active:translate-x-[2px] active:translate-y-[2px]"
            >
              {isKo ? '🚀 지금 바로 체험하기' : '🚀 Try it now'}
            </button>
            
            <p className="text-center text-sm text-white/40">
              {isKo 
                ? '로그인 없이 샘플 데이터로 먼저 체험해보세요'
                : 'Try with sample data without logging in'}
            </p>
          </div>

          {/* 하단 안내 */}
          <div className="mt-10 pt-6 border-t border-white/10 text-center">
            <p className="text-xs text-white/30">
              {isKo 
                ? '🎉 베타 기간 중 모든 기능 무료 • 신용카드 불필요'
                : '🎉 All features free during beta • No credit card required'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
